import React from 'react';
import L from 'leaflet';
import { buffer as turfBuffer } from '@turf/buffer';
import { lineString } from '@turf/helpers';

// Geofencing preset colors (zone/place/route paths and group colors).
// Mirrors /app/frontend/src/components/shared/groupColors.js — kept in sync so users
// see the same palette across the app.
export const PRESET_COLORS = [
  { value: '#2563eb', label: 'Blue' },
  { value: '#dc2626', label: 'Red' },
  { value: '#16a34a', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#0d9488', label: 'Teal' },
  { value: '#4f46e5', label: 'Indigo' },
  { value: '#9333ea', label: 'Purple' },
  { value: '#be185d', label: 'Pink' },
  { value: '#8B6914', label: 'Brown' },
  { value: '#475569', label: 'Slate' },
];

// Dot marker slugs -> hex colour (mirrors PRESET_COLORS); icon markers fall back to place green.
const DOT_COLOR_MAP = {
  'dot-red': '#dc2626',
  'dot-blue': '#2563eb',
  'dot-green': '#16a34a',
  'dot-orange': '#ea580c',
  'dot-purple': '#9333ea',
  'dot-amber': '#F59E0B',
  'dot-brown': '#8B6914',
};

export const getPlaceColor = (icon) => DOT_COLOR_MAP[icon] || '#10B981';

// Buffers a route polyline by its deviation distance. Returns an array of polygons,
// each polygon being an array of [lat,lng] rings (outer + holes), or null.
export const getRouteCorridor = (path, meters) => {
  const pts = (path || []).filter((c) => Number.isFinite(c?.[0]) && Number.isFinite(c?.[1]));
  const dist = Number(meters);
  if (pts.length < 2 || !Number.isFinite(dist) || dist <= 0) return null;
  try {
    const buffered = turfBuffer(lineString(pts.map(([lat, lng]) => [lng, lat])), dist / 1000, { units: 'kilometers' });
    const geom = buffered?.geometry;
    if (!geom) return null;
    const polys = geom.type === 'MultiPolygon' ? geom.coordinates : [geom.coordinates];
    return polys.map((rings) => rings.map((ring) => ring.map(([lng, lat]) => [lat, lng])));
  } catch {
    return null;
  }
};

export const formatArea = (sqm) => {
  if (!sqm) return '—';
  if (sqm >= 1000000) return `${(sqm / 1000000).toFixed(2)} km²`;
  return `${Math.round(sqm).toLocaleString()} m²`;
};

export const createPlaceIcon = (icon) => L.divIcon({
  className: 'custom-place-marker',
  html: `<img src="/place-icons/${icon}.svg" style="width:32px;height:32px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));" alt="" />`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

export const PlaceMarkerContent = ({ icon }) => (
  <img
    src={`/place-icons/${icon}.svg`}
    alt=""
    style={{ width: '32px', height: '32px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))', cursor: 'pointer' }}
  />
);

export const getZoneCoords = (zone) => {
  const geo = zone?.geometry;
  if (!geo) return { type: 'polygon', coords: [], center: null, radius: 0 };
  if (geo.type === 'circle') {
    return { type: 'circle', coords: [], center: geo.center, radius: geo.radius || 500 };
  }
  return { type: geo.type || 'polygon', coords: geo.coordinates || [], center: null, radius: 0 };
};

export const createZoneBounds = (zone) => {
  const geo = zone?.geometry;
  if (!geo) return null;

  if (geo.type === 'circle' && geo.center) {
    const radiusDegrees = (geo.radius || 500) / 111320;
    return L.latLngBounds(
      [geo.center[0] - radiusDegrees, geo.center[1] - radiusDegrees],
      [geo.center[0] + radiusDegrees, geo.center[1] + radiusDegrees],
    );
  }

  if (geo.coordinates?.length >= 2) {
    return L.latLngBounds(geo.coordinates.map((coord) => [coord[0], coord[1]]));
  }

  return null;
};

export const createPlaceBounds = (place) => {
  if (!place?.coordinates?.length) return null;
  const radiusDegrees = (place.radius || 100) / 111320;
  return L.latLngBounds(
    [place.coordinates[0] - radiusDegrees * 2, place.coordinates[1] - radiusDegrees * 2],
    [place.coordinates[0] + radiusDegrees * 2, place.coordinates[1] + radiusDegrees * 2],
  );
};

export const createRouteBounds = (route) => {
  if (!route?.path_coordinates?.length) return null;
  return L.latLngBounds(route.path_coordinates.map((coord) => [coord[0], coord[1]]));
};

export const toGoogleBounds = (bounds) => {
  if (!bounds || !window.google?.maps) return null;
  return new window.google.maps.LatLngBounds(
    { lat: bounds.getSouthWest().lat, lng: bounds.getSouthWest().lng },
    { lat: bounds.getNorthEast().lat, lng: bounds.getNorthEast().lng },
  );
};

export const groupItems = (items, groupField, groups, nameField) => {
  const grouped = {};
  const ungroupedItems = [];

  items.forEach((item) => {
    const groupId = item[groupField];
    if (!groupId) {
      ungroupedItems.push(item);
      return;
    }

    const matchingGroup = groups.find((group) => group.id === groupId);
    const groupName = matchingGroup?.name || 'Unknown Group';
    const groupColor = matchingGroup?.color || null;
    const groupOrder = matchingGroup?.display_order ?? 0;

    if (!grouped[groupName]) {
      grouped[groupName] = { id: groupId, color: groupColor, display_order: groupOrder, items: [] };
    }

    grouped[groupName].items.push(item);
  });

  const sortByName = (a, b) => (a[nameField] || '').localeCompare(b[nameField] || '');
  Object.values(grouped).forEach((group) => group.items.sort(sortByName));
  ungroupedItems.sort(sortByName);

  if (ungroupedItems.length > 0) {
    grouped.Ungrouped = { id: '__ungrouped__', color: null, display_order: Number.MAX_SAFE_INTEGER, items: ungroupedItems };
  }

  // App-wide convention (groups-ui-pattern.md §2.7): named groups sorted by display_order
  // (with name as tiebreaker), Ungrouped is ALWAYS last.
  const namedGroups = Object.keys(grouped).filter((name) => name !== 'Ungrouped');
  namedGroups.sort((a, b) => {
    const oa = grouped[a].display_order ?? 0;
    const ob = grouped[b].display_order ?? 0;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });
  const sortedGroups = [
    ...namedGroups,
    ...(ungroupedItems.length > 0 ? ['Ungrouped'] : []),
  ];

  return { sortedGroups, grouped };
};